import {
  Alert,
  Box,
  Button,
  FormHelperText,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";

import { EditOutlined } from "@mui/icons-material";

import { NumericFormat } from "react-number-format";

import { forwardRef, useEffect, useState } from "react";

import { useFetcher } from "react-router-dom";

import { first, isObject } from "radash";

import PricingVariableCard from "./PricingVariableCard";

const BASE_FLOOR_TIME_ON_SITES = ["15", "30"];

const BOTH_REQUIRED_MESSAGE = "Please enter rates for both 15 and 30s TOS";

const RegularTableCell = ({
  timeOnSite,
  value,
  valueFormatter = (v) => v,
  edit,
  state,
}) => {
  return (
    <TableCell
      sx={{ width: 100, ...(state === "changed" && { color: "info.main" }) }}
    >
      {!edit || state === "changed" ? valueFormatter(value, timeOnSite) : "-"}
    </TableCell>
  );
};

const EditableTableCell = forwardRef(
  ({ edit, value, state, onBaseRateChange, ...props }, ref) => {
    if (edit) {
      return (
        <TableCell sx={{ width: 100, p: 0 }}>
          <NumericFormat
            customInput={TextField}
            InputProps={{
              startAdornment: "$",
              sx: {
                borderRadius: 0,
                fontSize: "14px",
                ...(state === "changed" && { color: "info.main" }),
              },
            }}
            onChange={(e) => onBaseRateChange(e.target.value)}
            decimalScale={4}
            fullWidth
            {...(state === "error" && { error: true })}
            {...(state === "changed" && { color: "info", focused: true })}
            defaultValue={value}
          />
        </TableCell>
      );
    }

    return <RegularTableCell value={value} {...props} />;
  }
);

const mapTimeOnSites = (values, mapperFn) =>
  Object.keys(values)
    .sort()
    .map((key, idx) => mapperFn(key, values[key], idx));

const baseTimeOnSiteRateOf = (values) => {
  if (isObject(values)) {
    const timeOnSite = first(Object.keys(values).sort());

    return values[timeOnSite];
  }
};

const getResponseError = (response) => first(response?.errors?.base_rate || []);

// Base floors validate per-tier (rate_15 / rate_30 / base), so surface whichever error came back.
const getFirstResponseError = (response) => {
  const errors = response?.errors;

  if (!errors) {
    return null;
  }

  const firstKey = Object.keys(errors)[0];

  return first(errors[firstKey] || []);
};

const isFilled = (value) => value != null && value !== "";

const AudiencePricePerTimeOnSiteTable = ({
  data: dataFromProp,
  currentQuery,
  onEditModeChange,
  isEditing = false,
}) => {
  const [showEdit, setShowEdit] = useState(false);
  const [data, setData] = useState({ original: dataFromProp, ...dataFromProp });
  const [snackbar, setSnackbar] = useState(null);
  const validator = useFetcher();
  const submitter = useFetcher();

  const isBaseFloor = currentQuery?.floor_type === "base";

  const baseRateValues = isBaseFloor
    ? BASE_FLOOR_TIME_ON_SITES.map((tos) => data.base_rates?.[tos])
    : [];
  const baseFilledCount = baseRateValues.filter(isFilled).length;
  const baseBothOrNone =
    baseFilledCount === 0 || baseFilledCount === baseRateValues.length;
  const baseRequiredError =
    isBaseFloor && baseFilledCount === 1 ? BOTH_REQUIRED_MESSAGE : null;

  const handleCloseSnackbar = () => setSnackbar(null);

  const handleUpdate = (base_rate) => {
    const payload = { ...currentQuery, base_rate };

    submitter.submit(payload, {
      method: "post",
      action: `/managed_plus/pricing/account_audience_floors/${currentQuery.account_id}`,
    });
  };

  const handleBaseUpdate = () => {
    const payload = {
      floor_type: "base",
      product_type: currentQuery.product_type,
      rate_15: data.base_rates?.["15"] ?? "",
      rate_30: data.base_rates?.["30"] ?? "",
    };

    submitter.submit(payload, {
      method: "post",
      action: `/managed_plus/pricing/account_audience_floors/${currentQuery.account_id}`,
    });
  };

  const handleCancel = () => {
    setData(({ original }) => ({
      original,
      ...original,
      base_rate: baseTimeOnSiteRateOf(original.audience_floors),
      base_rates: { ...original.audience_floors },
    }));
    setSnackbar({
      children: "CANCELLED",
      severity: "error",
      sx: { bgcolor: ({ palette }) => palette.grey[700], color: "white" },
      color: "white",
    });
    setShowEdit(false);
  };

  useEffect(() => {
    setData({
      original: dataFromProp,
      ...dataFromProp,
      base_rate: baseTimeOnSiteRateOf(dataFromProp.audience_floors),
      base_rates: { ...dataFromProp.audience_floors },
    });
  }, [dataFromProp]);

  useEffect(() => {
    onEditModeChange?.(showEdit);
  }, [showEdit, onEditModeChange]);

  useEffect(() => {
    if (validator.state === "idle" && validator.data) {
      if ("audience_floors" in validator.data) {
        setData(({ original, base_rate }) => ({
          original,
          base_rate,
          ...validator.data,
          enableConfirm: true,
          state: "changed",
        }));
      } else {
        setData((prev) => ({
          ...prev,
          data_revenue_share: 0,
          error: getResponseError(validator.data),
          state: "error",
        }));
      }
    }
  }, [validator]);

  useEffect(() => {
    if (!isEditing && showEdit) {
      setShowEdit(false);
    }
  }, [isEditing, showEdit]);

  useEffect(() => {
    if (submitter.state === "idle" && submitter.data) {
      const response = submitter.data;

      // Fetchers keep their data after completion, and this effect re-runs when
      // isBaseFloor flips (e.g. switching floor type after a Confirm). Clear the
      // data once handled so the re-run doesn't re-apply a stale response.
      submitter.data = undefined;

      if (response.errors) {
        setData((prev) => ({
          ...prev,
          error: getFirstResponseError(response),
          state: "error",
        }));

        return;
      }

      if (isBaseFloor) {
        const floors = response;

        setData((prev) => ({
          ...prev,
          original: { audience_floors: floors },
          audience_floors: floors,
          base_rates: { ...floors },
          error: null,
          state: null,
        }));
      } else {
        setData(({ base_rate }) => ({
          original: response,
          base_rate,
          ...response,
        }));
      }

      setSnackbar({ children: "UPDATED", severity: "success" });
      setShowEdit(false);
    }
  }, [submitter, isBaseFloor]);

  return (
    <Stack>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Toolbar
          sx={{
            bgcolor: ({ palette }) =>
              isEditing ? "#A3BFFD" : palette.action.disabledBackground,
          }}
          variant="dense"
          component={Paper}
          square
        >
          <Typography variant="body2" component="div">
            CPC Floors
          </Typography>
        </Toolbar>
        <Table>
          <TableHead>
            <TableRow key="header">
              {mapTimeOnSites(
                data.audience_floors,
                (timeOnSite, value, idx) => (
                  <RegularTableCell
                    key={timeOnSite}
                    value={`${timeOnSite}s`}
                    variant="head"
                  />
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow key="values">
              {mapTimeOnSites(
                data.audience_floors,
                (timeOnSite, value, idx) => {
                  const editable = isBaseFloor || idx === 0;

                  return editable ? (
                    <EditableTableCell
                      key={timeOnSite}
                      timeOnSite={timeOnSite}
                      value={value}
                      edit={showEdit}
                      state={data.state}
                      onBaseRateChange={(newValue) => {
                        if (isBaseFloor) {
                          setData((prev) => ({
                            ...prev,
                            base_rates: {
                              ...prev.base_rates,
                              [timeOnSite]: newValue,
                            },
                            error: null,
                            state: null,
                          }));
                        } else {
                          setData(({ ...prev }) => ({
                            ...prev,
                            base_rate: newValue,
                            enableConfirm: false,
                            state: null,
                          }));
                        }
                      }}
                      valueFormatter={(v) =>
                        (value && `$${value}`) ?? <>&nbsp;</>
                      }
                      variant="body"
                    />
                  ) : (
                    <RegularTableCell
                      key={timeOnSite}
                      timeOnSite={timeOnSite}
                      edit={showEdit}
                      valueFormatter={(v) =>
                        (value && `$${value}`) ?? <>&nbsp;</>
                      }
                      value={value}
                      variant="body"
                      state={data.state}
                    />
                  );
                }
              )}
            </TableRow>
          </TableBody>
        </Table>
        <FormHelperText error>
          {(isBaseFloor ? (baseRequiredError ?? data.error) : data.error) ?? " "}
        </FormHelperText>
        <Toolbar sx={{ p: 0 }} variant="regular" disableGutters>
          {isEditing &&
            (!showEdit ? (
              <Button
                variant="contained"
                onClick={(e) => {
                  if (isBaseFloor) {
                    setData((prev) => ({
                      ...prev,
                      base_rates: { ...prev.audience_floors },
                      error: null,
                      state: null,
                    }));
                  }
                  setShowEdit(true);
                }}
              >
                Update
              </Button>
            ) : (
              !isBaseFloor && (
                <Button
                  variant="contained"
                  type="submit"
                  onClick={(e) => {
                    validator.submit(
                      { base_rate: data.base_rate, ...currentQuery },
                      {
                        action:
                          "/managed-plus/pricing/preview_floors_changes/account",
                        method: "post",
                        formEncType: "application/json",
                      }
                    );
                  }}
                >
                  Recalculate
                </Button>
              )
            ))}
        </Toolbar>
      </Box>
      {isBaseFloor ? (
        showEdit && (
          <Box sx={{ display: "flex", direction: "row", pt: 2, pb: 2 }}>
            <Button
              disabled={!baseBothOrNone}
              color="primary2"
              variant="contained"
              onClick={handleBaseUpdate}
            >
              Confirm
            </Button>
            <Button
              variant="contained"
              onClick={handleCancel}
              color="primary2"
              sx={{ ml: 2 }}
            >
              Cancel
            </Button>
          </Box>
        )
      ) : (
        <PricingVariableCard
          state={data.state}
          dataRevenueShare={data?.data_revenue_share?.toString()}
          showActionButtons={showEdit}
          disableConfirm={!data.enableConfirm || data.state === "error"}
          onConfirm={() => handleUpdate(data.base_rate)}
          onCancel={handleCancel}
        />
      )}
      <Box sx={{ display: "flex", justifyContent: "right" }}>
        {showEdit && (
          <Alert variant="filled" icon={<EditOutlined />} color="info" sx={{ backgroundColor: "#558BF7" }}>
            EDIT MODE
          </Alert>
        )}
      </Box>
      {!!snackbar && (
        <Snackbar
          open
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          autoHideDuration={6000}
        >
          <Alert variant="filled" {...snackbar} onClose={handleCloseSnackbar} />
        </Snackbar>
      )}
    </Stack>
  );
};

export default AudiencePricePerTimeOnSiteTable;
